/**
 * Puzzle 5 — Final Blockage.
 *
 * A rusted blockage seals off the Elder's grove. Both cats must use the
 * newly-learned (from Elder Leo) Resonant Drop co-op ability to shatter
 * it. The puzzle listens for `RESONANT_DROP_TRIGGERED` and, if the drop
 * lands inside the blockage's shockwave radius, calls `shatter()`.
 *
 * The win condition also gates on the other four puzzles being solved —
 * this enforces the narrative sequencing where the Elder only arrives
 * after the rest of the scrapyard is restored. We read from
 * `ctx.levelState`, matching the ClockworkCanopy apex pattern.
 *
 * Elder Leo encounter + exit portal remain in the scene shell (they are
 * level-wide concerns, not a puzzle element).
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { RustedBlockage } from "../../../entities/interactives/RustedBlockage";

import { GameEvents } from "../../../config/events";
import { SCRAPYARD } from "../../../config/GameConfig";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

/** Puzzle IDs this puzzle gates on. All must report `solved: true`. */
const REQUIRED_PUZZLES = [
  "seizedCrane",
  "crowSquirrel",
  "kittenCorral",
  "hoodieJigsaw",
] as const;

const CUE = {
  gather: { cat: "both", parts: [{ text: "side by side at the blockage" }] },
  drop: { cat: "both", parts: [{ action: "resonantDrop" }, { text: "together: shatter it" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Shatter the rusted blockage";
const BLOCKAGE_ANCHOR = { x: 140, y: 5, z: 0 };
/** Both cats this close to the blockage count as "at" it for the cue. */
const AT_BLOCKAGE = 7;

export class FinalBlockagePuzzle implements PuzzleModule {
  readonly id = "finalBlockage";
  readonly displayName = "Final Blockage";

  private ctx!: PuzzleContext;

  private finalBlockage!: RustedBlockage;
  private shattered = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Rusted blockage ──
    this.finalBlockage = new RustedBlockage(scene, {
      key: "finalBlockage",
      position: new Vector3(140, 3, 0),
      label: "Rusted Blockage",
    });

    ctx.registerInteractive({
      mesh: this.finalBlockage.rootMesh,
      hidden: false,
      state: "revealed",
    });

    // ── Resonant Drop shockwave: shatter if inside radius ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, (data) => {
        if (this.shattered || this.finalBlockage.isShattered) return;

        // Gate on prior puzzles: mirror ClockworkCanopy's apex gating so
        // players can't skip ahead even if they obtain Resonant Drop
        // through a save file restore.
        const allPriorSolved = REQUIRED_PUZZLES.every(
          (id) => ctx.levelState.get(id)?.solved === true,
        );
        if (!allPriorSolved) return;

        const epicenter = new Vector3(data.x, data.y, data.z);
        const dist = Vector3.Distance(
          epicenter,
          this.finalBlockage.rootMesh.position,
        );
        if (dist <= data.radius + 2.5) {
          this.finalBlockage.shatter();
          // Forensic-QA event: confirms the shockwave→blockage hit. The
          // subsequent BLOCKAGE_SHATTERED emit from RustedBlockage.shatter()
          // is generic (can fire from other paths); SHOCKWAVE_SHATTERED
          // narrows it to "Resonant Drop landed on a blockage and cleared it."
          ctx.gameEvents.emit(GameEvents.SHOCKWAVE_SHATTERED, {
            x: data.x,
            y: data.y,
            z: data.z,
            radius: data.radius,
            blockageKey: "finalBlockage",
          });
        }
      }),
    );

    // ── Blockage shattered → puzzle solved ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.BLOCKAGE_SHATTERED, (data) => {
        if (data?.key !== "finalBlockage") return;
        if (this.shattered) return;
        this.shattered = true;
        ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      }),
    );
  }

  update(_dt: number, _elapsed: number): void {
    // No per-frame logic — all event driven.
  }

  getCue(): PuzzleCue | null {
    if (this.shattered) return null;
    const { minna, basil } = this.ctx;
    const at = this.finalBlockage.rootMesh.position;
    const ready =
      Vector3.Distance(minna.mesh.position, basil.mesh.position) <= SCRAPYARD.PUZZLE.RESONANT_DROP_RANGE &&
      Vector3.Distance(minna.mesh.position, at) < AT_BLOCKAGE &&
      Vector3.Distance(basil.mesh.position, at) < AT_BLOCKAGE;
    return { objective: OBJECTIVE, cue: ready ? CUE.drop : CUE.gather, anchor: BLOCKAGE_ANCHOR };
  }

  getState(): PuzzleState {
    if (this.shattered) return { solved: true, progress: 1 };
    // Partial progress: fraction of prior puzzles complete (apex gating).
    const priorSolved = REQUIRED_PUZZLES.filter(
      (id) => this.ctx?.levelState.get(id)?.solved,
    ).length;
    return {
      solved: false,
      progress: (priorSolved / REQUIRED_PUZZLES.length) * 0.9,
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "finalBlockage", position: { x: 140, y: 3 }, radius: 2.0, allowedCat: "either", label: "Final blockage (Resonant Drop target)" },
        // Apex Resonant Drop is a cooperative ability — traces typically
        // stage both cats adjacent to the blockage, so a cat-agnostic
        // staging target is useful.
        { id: "drop_stage", position: { x: 138, y: 3 }, radius: 1.5, allowedCat: "either", label: "Resonant Drop staging" },
      ],
      traversals: [
        // FinalBlockage is a co-op gate — both cats stand adjacent and
        // press f+m simultaneously. PUZZLE_ORIGINS warps both cats to
        // (138, 1) on the ground, well within RESONANT_DROP_RANGE of
        // the blockage. No staircase or mid-puzzle traversal needed —
        // the f+m chord IS the entire mechanic. This recipe documents
        // the canonical input chord so the trace runner can dispatch
        // it via `traverse` if a future plan prefers recipe-style.
        {
          id: "coop_resonant_drop",
          kind: "coop",
          cat: "either",
          from: "drop_stage",
          to: "finalBlockage",
          successWindowMs: 3_000,
          recipe: [
            // f+m composite — both keys held together for 200ms so
            // AbilityInputMapper's chord synthesis sees both as
            // pressed in the same frame.
            { keyDown: "f" },
            { keyDown: "m" },
            { waitMs: 200 },
            { keyUp: "m" },
            { keyUp: "f" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   { solved?: boolean }  // shatter blockage + emit PUZZLE_SOLVED
   *
   * Only useful seed is the full solve — FinalBlockage has no intermediate
   * sub-steps (it gates on every other L1 puzzle + a single Resonant Drop).
   * For a trace testing the Elder encounter, `{ solved: true }` skips
   * straight to the post-shatter state.
   */
  seed(state: Record<string, unknown>): void {
    if (state.solved === true && !this.shattered) {
      // Mirror the RESONANT_DROP_TRIGGERED branch: shatter the blockage
      // (which emits BLOCKAGE_SHATTERED, caught by our own handler to
      // emit PUZZLE_SOLVED).
      if (!this.finalBlockage.isShattered) {
        this.finalBlockage.shatter();
      }
      if (!this.shattered) {
        // Defensive: if something swallowed the BLOCKAGE_SHATTERED event,
        // still set our flag + fire the solve event so levelState is right.
        this.shattered = true;
        this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      }
    }
  }

  dispose(): void {
    this.finalBlockage.dispose();
  }
}
